import { useState } from "react";
import { Loader2, PhoneCall, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";

const REASONS = [
  ["choosing_a_course", "Choosing a course"],
  ["understanding_eligibility", "Understanding eligibility"],
  ["application_help", "Application help"],
  ["documents_evidence", "Documents/evidence"],
  ["funding", "Funding"],
  ["international_study", "International study"],
  ["understanding_an_opportunity", "Understanding an opportunity"],
  ["something_else", "Something else"],
];

const inputClass = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";

export const RequestCallModal = ({ opportunitySlug, applicationId, sourcePage, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    mobile: "",
    preferred_contact_method: "email",
    reason: "",
    message: "",
    preferred_time: "",
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState("");

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async () => {
    setSaving(true);
    try {
      const response = await api.post("/call-requests", {
        ...form,
        opportunity_slug: opportunitySlug || undefined,
        application_id: applicationId || undefined,
        source_page: sourcePage,
      });
      setDone(response.data.message);
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const valid = form.name.trim().length >= 2 && (form.email.trim() || form.mobile.trim()) && form.reason;

  return (
    <div data-testid="request-call-modal" className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/50 sm:items-center sm:p-6" role="dialog" aria-modal="true">
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-xs font-bold text-cobalt"><PhoneCall className="h-4 w-4" /> Human support</p>
            <h2 className="mt-3 font-display text-2xl font-bold text-ink">Request a call from our team</h2>
            <p className="mt-2 text-sm leading-6 text-slatey">Need some help? Request a call from the OpenDegree team and we’ll get back to you.</p>
          </div>
          <button onClick={onClose} data-testid="request-call-close" className="rounded-lg p-2 text-slatey hover:bg-slate-50"><X className="h-5 w-5" /></button>
        </div>

        {done ? (
          <div className="mt-6 rounded-2xl bg-emerald-50 p-5" data-testid="request-call-success">
            <p className="text-sm font-bold leading-6 text-emerald-800">{done}</p>
            <button onClick={onClose} data-testid="request-call-done" className="mt-4 rounded-full bg-cobalt px-5 py-2.5 text-sm font-bold text-white">Done</button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-bold text-slatey">Name</label><input value={form.name} onChange={set("name")} data-testid="request-call-name" className={inputClass} placeholder="Your name" /></div>
              <div><label className="mb-1 block text-xs font-bold text-slatey">Preferred contact method</label>
                <select value={form.preferred_contact_method} onChange={set("preferred_contact_method")} data-testid="request-call-method" className={inputClass}>
                  <option value="email">Email</option><option value="mobile">Phone / text</option>
                </select>
              </div>
              <div><label className="mb-1 block text-xs font-bold text-slatey">Email</label><input value={form.email} onChange={set("email")} data-testid="request-call-email" type="email" className={inputClass} placeholder="you@example.com" /></div>
              <div><label className="mb-1 block text-xs font-bold text-slatey">Mobile number</label><input value={form.mobile} onChange={set("mobile")} data-testid="request-call-mobile" type="tel" className={inputClass} placeholder="+44…" /></div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-slatey">What do you need help with?</label>
              <select value={form.reason} onChange={set("reason")} data-testid="request-call-reason" className={inputClass}>
                <option value="">Choose a reason</option>
                {REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-bold text-slatey">Preferred time/day (optional)</label><input value={form.preferred_time} onChange={set("preferred_time")} data-testid="request-call-time" className={inputClass} placeholder="For example: weekday afternoons" /></div>
              <div><label className="mb-1 block text-xs font-bold text-slatey">Message (optional)</label><input value={form.message} onChange={set("message")} data-testid="request-call-message" className={inputClass} placeholder="Anything we should know?" /></div>
            </div>
            <button onClick={submit} disabled={!valid || saving} data-testid="request-call-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-6 py-3.5 text-sm font-bold text-white disabled:opacity-50">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Request a call
            </button>
            {!user && <p className="text-center text-xs leading-5 text-slatey/80">No account needed — we only use these details to respond to your request.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export const RequestCallButton = ({ className = "", testId = "request-call-button", children, ...modalProps }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testId} className={className || "inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-ink transition-colors hover:border-cobalt"}>
        <PhoneCall className="h-4 w-4 text-cobalt" /> {children || "Request a call from our team"}
      </button>
      {open && <RequestCallModal {...modalProps} onClose={() => setOpen(false)} />}
    </>
  );
};
